// AttestPage: per-condition data-attestation widgets.
//
// Slice 4 only shipped the session-wide rollup ops (AssertStructural +
// ComputeLevel). The integrity catalog has three data-attested
// conditions that need per-subject inputs:
//
//   D-001 (grain uniqueness)      — per schema
//   D-002 (FD-edge attestation)    — per (table, X, Y)
//   D-005 (sibling-coherence)      — per (root, sibling, anchor, reducer)
//                                    OR opt-out for the deferred cross-
//                                    table-join case
//
// Slice 7 builds the three forms. Each posts to the backend op; the
// result shows holds / violations inline; the Verify page's condition
// table updates next time the user opens it.

import { useCallback, useEffect, useState } from "react";
import { ApiError, type DraftResponse } from "../api/client";
import type { ListTablesResult } from "../api/types";
import { useSession } from "./SessionContext";

export function AttestPage() {
  const { client, session, refreshSession } = useSession();
  const [draft, setDraft] = useState<DraftResponse["draft_ac"]>(null);
  const [tables, setTables] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grainResult, setGrainResult] = useState<GrainResultType | null>(null);
  const [fdResult, setFDResult] = useState<FDResultType | null>(null);
  const [siblingResult, setSiblingResult] = useState<SiblingResultType | null>(
    null,
  );

  const loadContext = useCallback(async () => {
    if (session === null) return;
    try {
      const [d, t] = await Promise.all([
        client.getDraft(session.session_id),
        client.runOp(session.session_id, "ListTables", { inputs: {} }),
      ]);
      setDraft(d.draft_ac);
      if (t.success && t.result) {
        const res = t.result as unknown as ListTablesResult;
        setTables([...res.tables].sort());
      }
    } catch (e) {
      setError(formatError(e));
    }
  }, [client, session?.session_id]);

  useEffect(() => {
    setDraft(null);
    setTables([]);
    setError(null);
    setGrainResult(null);
    setFDResult(null);
    setSiblingResult(null);
    if (session !== null) loadContext();
  }, [session, loadContext]);

  async function runAttest<T>(opName: string, inputs: Record<string, unknown>): Promise<T | null> {
    if (session === null) return null;
    setBusy(true);
    setError(null);
    try {
      const r = await client.runOp(session.session_id, opName, { inputs });
      if (!r.success || !r.result) throw new Error(r.error ?? `${opName} failed`);
      refreshSession();
      return r.result as T;
    } catch (e) {
      setError(formatError(e));
      return null;
    } finally {
      setBusy(false);
    }
  }

  if (session === null) {
    return (
      <section>
        <h1>Attest</h1>
        <p style={{ color: "#888" }}>
          Create a session on the <em>Session</em> tab first.
        </p>
      </section>
    );
  }

  const draftSchemas = draft?.schemas ?? [];

  return (
    <section>
      <h1>Attest</h1>
      <p style={subtitleStyle}>
        Per-condition data attestation. Each form runs the corresponding
        op against the backend; the Verify page picks up the updated
        condition statuses.
      </p>

      {error && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <GrainSection
        disabled={busy}
        schemas={draftSchemas.map((s) => s.schema_name)}
        onAttest={async (schemaName) => {
          const r = await runAttest<GrainResultType>(
            "AttestGrainUniqueness",
            { schema_name: schemaName },
          );
          if (r) setGrainResult(r);
        }}
        lastResult={grainResult}
      />

      <FDEdgeSection
        disabled={busy}
        tables={tables}
        onAttest={async (table, determinant, dependent) => {
          const r = await runAttest<FDResultType>("AttestFDEdge", {
            table,
            determinant,
            dependent,
          });
          if (r) setFDResult(r);
        }}
        lastResult={fdResult}
      />

      <SiblingCoherenceSection
        disabled={busy}
        tables={tables}
        onAttest={async (inputs) => {
          const r = await runAttest<SiblingResultType>(
            "AttestSiblingCoherence",
            inputs,
          );
          if (r) setSiblingResult(r);
        }}
        lastResult={siblingResult}
      />
    </section>
  );
}

// ── Result types from the backend ─────────────────────────────────────

interface GrainResultType {
  schema_name: string;
  grain_columns: string[];
  table: string;
  holds: boolean;
  duplicate_count: number;
  row_count: number;
}

interface FDResultType {
  table: string;
  determinant: string;
  dependent: string;
  holds: boolean;
  cache_key: string;
}

interface SiblingResultType {
  holds: boolean;
  opted_out: boolean;
  cache_key: string;
}

// ── GrainSection (D-001) ──────────────────────────────────────────────

function GrainSection({
  disabled,
  schemas,
  onAttest,
  lastResult,
}: {
  disabled: boolean;
  schemas: string[];
  onAttest: (schemaName: string) => void;
  lastResult: GrainResultType | null;
}) {
  const [pick, setPick] = useState("");
  return (
    <FormBlock title="Grain uniqueness (D-001)">
      <p style={hintStyle}>
        For one schema in the draft AC: verify its grain columns uniquely
        identify rows in the bound table. Updates <code>D-001</code>.
      </p>
      {schemas.length === 0 ? (
        <p style={{ color: "#888" }}>
          Draft AC has no schemas yet. Add one on the <em>Declare</em> tab.
        </p>
      ) : (
        <div style={formRowStyle}>
          <label style={labelStyle}>
            schema
            <select
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              style={selectStyle}
            >
              <option value="">— pick —</option>
              {schemas.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => pick !== "" && onAttest(pick)}
            disabled={disabled || pick === ""}
            style={buttonStyle}
          >
            Attest grain
          </button>
        </div>
      )}
      {lastResult && (
        <div style={resultBlockStyle(lastResult.holds)}>
          {lastResult.holds ? "✓" : "✗"} Schema{" "}
          <code>{lastResult.schema_name}</code> grain ({lastResult.grain_columns.join(", ")})
          {" "}— {lastResult.holds ? "unique" : `${lastResult.duplicate_count} duplicate row(s)`}{" "}
          across {lastResult.row_count.toLocaleString()} rows in{" "}
          <code>{lastResult.table}</code>.
        </div>
      )}
    </FormBlock>
  );
}

// ── FDEdgeSection (D-002) ─────────────────────────────────────────────

function FDEdgeSection({
  disabled,
  tables,
  onAttest,
  lastResult,
}: {
  disabled: boolean;
  tables: string[];
  onAttest: (table: string, determinant: string, dependent: string) => void;
  lastResult: FDResultType | null;
}) {
  const [table, setTable] = useState("");
  const [determinant, setDeterminant] = useState("");
  const [dependent, setDependent] = useState("");
  return (
    <FormBlock title="FD-edge attestation (D-002)">
      <p style={hintStyle}>
        For a backend table + a column pair (X, Y): verify that every
        distinct X value maps to exactly one Y. Sticky-failure: one
        failed attestation pins D-002 until the session is reset.
      </p>
      <div style={formRowStyle}>
        <label style={labelStyle}>
          table
          <select
            value={table}
            onChange={(e) => setTable(e.target.value)}
            style={selectStyle}
          >
            <option value="">— pick —</option>
            {tables.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label style={labelStyle}>
          determinant (X)
          <input
            value={determinant}
            onChange={(e) => setDeterminant(e.target.value)}
            placeholder="store_id"
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          dependent (Y)
          <input
            value={dependent}
            onChange={(e) => setDependent(e.target.value)}
            placeholder="city"
            style={inputStyle}
          />
        </label>
        <button
          onClick={() => {
            if (table === "" || determinant.trim() === "" || dependent.trim() === "") return;
            onAttest(table, determinant.trim(), dependent.trim());
          }}
          disabled={disabled || table === "" || determinant.trim() === "" || dependent.trim() === ""}
          style={buttonStyle}
        >
          Attest edge
        </button>
      </div>
      {lastResult && (
        <div style={resultBlockStyle(lastResult.holds)}>
          {lastResult.holds ? "✓" : "✗"} <code>{lastResult.determinant}</code> →{" "}
          <code>{lastResult.dependent}</code> in <code>{lastResult.table}</code>{" "}
          {lastResult.holds ? "holds" : "does NOT hold"}.{" "}
          <span style={{ color: "#888", fontSize: "0.85rem" }}>
            (cache key: <code>{lastResult.cache_key}</code>)
          </span>
        </div>
      )}
    </FormBlock>
  );
}

// ── SiblingCoherenceSection (D-005) ───────────────────────────────────

function SiblingCoherenceSection({
  disabled,
  tables,
  onAttest,
  lastResult,
}: {
  disabled: boolean;
  tables: string[];
  onAttest: (inputs: Record<string, unknown>) => void;
  lastResult: SiblingResultType | null;
}) {
  const [mode, setMode] = useState<"attest" | "opt_out">("attest");

  // Data-attested path inputs.
  const [rootTable, setRootTable] = useState("");
  const [rootColumn, setRootColumn] = useState("");
  const [sibTable, setSibTable] = useState("");
  const [sibColumn, setSibColumn] = useState("");
  const [anchor, setAnchor] = useState("");
  const [reducer, setReducer] = useState("SUM");

  // Opt-out path.
  const [optReason, setOptReason] = useState("");

  return (
    <FormBlock title="Sibling-coherence (D-005)">
      <p style={hintStyle}>
        The AAA gate. For each cross-schema family-root sibling pair:
        verify the sibling equals the family-root rolled-up via the
        family's ip_reducer at the sibling's anchor. The opt-out path
        marks D-005 satisfied via the <code>opted-out</code> warrant —
        legitimate for the cases the SQLite backend can't yet attest
        (the cross-table-join scenario in the retail demo).
      </p>

      <div style={{ marginBottom: "0.5rem" }}>
        <label style={radioLabelStyle}>
          <input
            type="radio"
            name="sibling-mode"
            checked={mode === "attest"}
            onChange={() => setMode("attest")}
          />{" "}
          Data-attested
        </label>
        <label style={radioLabelStyle}>
          <input
            type="radio"
            name="sibling-mode"
            checked={mode === "opt_out"}
            onChange={() => setMode("opt_out")}
          />{" "}
          Opt out (with reason)
        </label>
      </div>

      {mode === "attest" ? (
        <>
          <div style={formRowStyle}>
            <label style={labelStyle}>
              root_table
              <select
                value={rootTable}
                onChange={(e) => setRootTable(e.target.value)}
                style={selectStyle}
              >
                <option value="">—</option>
                {tables.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>
              root_column
              <input
                value={rootColumn}
                onChange={(e) => setRootColumn(e.target.value)}
                placeholder="revenue"
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              reducer
              <select
                value={reducer}
                onChange={(e) => setReducer(e.target.value)}
                style={selectStyle}
              >
                <option>SUM</option>
              </select>
            </label>
          </div>
          <div style={formRowStyle}>
            <label style={labelStyle}>
              sibling_table
              <select
                value={sibTable}
                onChange={(e) => setSibTable(e.target.value)}
                style={selectStyle}
              >
                <option value="">—</option>
                {tables.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>
              sibling_column
              <input
                value={sibColumn}
                onChange={(e) => setSibColumn(e.target.value)}
                placeholder="total_revenue"
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              sibling_anchor (CSV)
              <input
                value={anchor}
                onChange={(e) => setAnchor(e.target.value)}
                placeholder="region, day"
                style={inputStyle}
              />
            </label>
            <button
              onClick={() => {
                const cols = anchor
                  .split(",")
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0);
                if (
                  rootTable === "" || rootColumn.trim() === "" ||
                  sibTable === "" || sibColumn.trim() === "" || cols.length === 0
                ) return;
                onAttest({
                  root_table: rootTable,
                  root_column: rootColumn.trim(),
                  sibling_table: sibTable,
                  sibling_column: sibColumn.trim(),
                  sibling_anchor: cols,
                  reducer,
                });
              }}
              disabled={disabled}
              style={buttonStyle}
            >
              Attest
            </button>
          </div>
          <p style={hintStyle}>
            SQLite backend (Phase 2): anchor columns must exist in BOTH
            tables. Cross-table-join cases (e.g., transactions →
            region_daily_summary at (region, day)) need a transactions⋈stores
            join the backend doesn't yet support — use the opt-out path for
            those until Phase 6 (execution surface).
          </p>
        </>
      ) : (
        <div style={formRowStyle}>
          <label style={{ ...labelStyle, flex: 1 }}>
            opt_out_reason
            <input
              value={optReason}
              onChange={(e) => setOptReason(e.target.value)}
              placeholder="The cross-table-join case is deferred to Phase 6"
              style={{ ...inputStyle, minWidth: "32rem" }}
            />
          </label>
          <button
            onClick={() => {
              if (optReason.trim() === "") return;
              onAttest({ opt_out_reason: optReason.trim() });
            }}
            disabled={disabled || optReason.trim() === ""}
            style={buttonStyle}
          >
            Mark opted-out
          </button>
        </div>
      )}

      {lastResult && (
        <div style={resultBlockStyle(lastResult.holds)}>
          {lastResult.opted_out ? "○" : lastResult.holds ? "✓" : "✗"} D-005{" "}
          {lastResult.opted_out
            ? "marked opted-out"
            : lastResult.holds
            ? "holds"
            : "does NOT hold"}
          .{" "}
          <span style={{ color: "#888", fontSize: "0.85rem" }}>
            (cache key: <code>{lastResult.cache_key}</code>)
          </span>
        </div>
      )}
    </FormBlock>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function FormBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={formBlockStyle}>
      <h2 style={{ marginTop: 0, marginBottom: "0.75rem" }}>{title}</h2>
      {children}
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Styles ────────────────────────────────────────────────────────────

const subtitleStyle: React.CSSProperties = { color: "#888", margin: "0 0 1rem 0" };
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  marginBottom: "1rem",
  fontSize: "0.9rem",
};
const formBlockStyle: React.CSSProperties = {
  padding: "1rem 1.25rem",
  background: "#fff",
  border: "1px solid #ddd",
  borderRadius: 4,
  marginBottom: "1rem",
};
const formRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "1rem",
  alignItems: "flex-end",
  flexWrap: "wrap",
  marginBottom: "0.5rem",
};
const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  fontSize: "0.85rem",
  color: "#444",
};
const radioLabelStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.3rem",
  marginRight: "1rem",
  fontSize: "0.9rem",
  color: "#444",
};
const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.6rem",
  fontSize: "0.95rem",
  border: "1px solid #ccc",
  borderRadius: 4,
  minWidth: "12rem",
};
const selectStyle: React.CSSProperties = { ...inputStyle, minWidth: "10rem" };
const buttonStyle: React.CSSProperties = {
  padding: "0.5rem 1rem",
  background: "#0366d6",
  color: "white",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.95rem",
};
const hintStyle: React.CSSProperties = {
  margin: "0 0 0.5rem 0",
  fontSize: "0.85rem",
  color: "#666",
};

function resultBlockStyle(holds: boolean): React.CSSProperties {
  const ok = holds;
  return {
    padding: "0.6rem 0.8rem",
    background: ok ? "#e6ffed" : "#ffeef0",
    border: `1px solid ${ok ? "#a5d8b8" : "#f3c0c0"}`,
    borderRadius: 4,
    marginTop: "0.5rem",
    fontSize: "0.9rem",
    color: ok ? "#1a6b3a" : "#a02020",
  };
}
